import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DateFormatService } from '@lx/shared/i18n';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { OverlayOptions } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectorField } from '@lx/quote-tool/lite/shared/ui/directives';
import { getCustomFormComponentControlValueAccessorProvider } from '@lx/quote-tool/lite/shared/utils';
import { ClickOutsideDirective } from '@lx/shared/ui/directives';
import { KeyboardFocusDirective } from '@lx/shared/ui/keyboard-focus';

@Component({
  standalone: true,
  selector: 'lx-date-of-birth-field',
  imports: [DropdownModule, FormsModule, InputNumberModule, InputMaskModule, ClickOutsideDirective, KeyboardFocusDirective],
  templateUrl: './date-of-birth-field.component.html',
  styleUrls: ['./date-of-birth-field.component.scss'],
  providers: [getCustomFormComponentControlValueAccessorProvider(DateOfBirthFieldComponent)],
})
export class DateOfBirthFieldComponent extends SelectorField<string> implements OnInit {
  months: { name: string; code: number }[];
  day: string | undefined;
  month: number | undefined;
  year: number | undefined;

  overlayOption: OverlayOptions = {
    autoZIndex: true,
    baseZIndex: -1000,
  };
  monthScrollHeight = '500px';
  errorMessage = '';

  private interactionDetected = false;

  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.prepareMonths();
    this.breakpointObserverService.isMobile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isMobile) => {
      if (isMobile) {
        this.monthScrollHeight = '200px';
      } else {
        this.monthScrollHeight = '500px';
      }
    });
  }

  markTouchedOnOutsideFocus() {
    if (!this.interactionDetected || this.touched) {
      return;
    }
    this.markAsTouched();
  }

  writeValue(value: string | null): void {
    if (value) {
      const [year, month, day] = value.split('-');

      if (!this.month) {
        this.month = Number(month) - 1; // Adjust since months are 0-based in JS
      }

      if (!this.year) {
        this.year = Number(year);
      }

      if (!this.day) {
        this.day = day;
      }
    }
  }

  updateMonthOfDateOfBirth() {
    this.updateDateOfBirth();
  }

  updateDateOfBirth() {
    const day = this.day ? Number(this.day) : null;
    const month = this.month ?? null;
    const year = this.year ? Number(this.year) : null;

    // If ANY field is missing → date is incomplete
    if ([day, month, year].includes(null)) {
      this.onSelectValue(null);
      return;
    }

    if (!this.validateDate(day, month, year)) {
      this.onSelectValue(null);
      return;
    }

    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    this.onSelectValue(dateString);
  }

  validateDate(day: number, month: number, year: number): boolean {
    // Corrected Date Creation
    const date = new Date(year, month, day);
    const today = new Date();

    return date < today && date.getFullYear() === year && date.getMonth() === month && date.getDate() === day && year.toString().length === 4;
  }

  onInternalInteraction() {
    this.interactionDetected = true;
  }

  private prepareMonths() {
    const calendarConfig = DateFormatService.getCalendarLocaleSettings().monthNames;
    this.months = calendarConfig.map((name, index) => ({
      name,
      code: index,
    }));
  }
}
